import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { cn, getInitials, formatNumber } from '../lib/utils';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Users, Plus, Settings, Loader2, Search, 
  Lock, Globe, Crown, Shield, UserPlus, UserMinus, 
  MoreVertical, LogOut, Trash2, Edit, Image
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create Community Modal Component
function CreateCommunityModal({ open, onClose, onSuccess }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Community name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/communities`,
        { name, description, is_private: isPrivate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Community created!');
      onSuccess(response.data);
      onClose();
      setName('');
      setDescription('');
      setIsPrivate(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create community');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Create Community
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Name *</label>
            <Input
              placeholder="Community name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
              data-testid="community-name-input"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Description</label>
            <Textarea
              placeholder="What is this community about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={300}
            />
          </div>
          
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
            <div className="flex items-center gap-3">
              {isPrivate ? <Lock className="w-5 h-5 text-yellow-500" /> : <Globe className="w-5 h-5 text-green-500" />}
              <div>
                <p className="font-medium">{isPrivate ? 'Private' : 'Public'}</p>
                <p className="text-xs text-muted-foreground">
                  {isPrivate ? 'Only invited members can join' : 'Anyone can join'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPrivate(!isPrivate)}
            >
              {isPrivate ? 'Make Public' : 'Make Private'}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !name.trim()}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Communities Page
export default function CommunitiesPage() {
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [communities, setCommunities] = useState([]);
  const [myCommunities, setMyCommunities] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchCommunities = useCallback(async () => {
    try {
      const [allRes, myRes] = await Promise.all([
        axios.get(`${API}/communities`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/communities/my`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCommunities(allRes.data);
      setMyCommunities(myRes.data);
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCommunities();
  }, [fetchCommunities]);

  const handleJoin = async (communityId) => {
    try {
      await axios.post(
        `${API}/communities/${communityId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Joined community!');
      fetchCommunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleLeave = async (communityId) => {
    try {
      await axios.post(
        `${API}/communities/${communityId}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Left community');
      fetchCommunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave');
    }
  };

  const displayedCommunities = activeTab === 'my' ? myCommunities : communities;
  const filteredCommunities = displayedCommunities.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="communities-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-bold text-lg">Communities</h1>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)} 
            size="sm" 
            className="rounded-full"
            data-testid="create-community-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Create
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search communities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 rounded-full bg-secondary/50"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'all' ? 'default' : 'outline'}
            size="sm"
            className="rounded-full"
            onClick={() => setActiveTab('all')}
          >
            <Globe className="w-4 h-4 mr-1" />
            All Communities
          </Button>
          <Button
            variant={activeTab === 'my' ? 'default' : 'outline'}
            size="sm"
            className="rounded-full"
            onClick={() => setActiveTab('my')}
          >
            <Users className="w-4 h-4 mr-1" />
            My Communities ({myCommunities.length})
          </Button>
        </div>

        {/* Communities List */}
        {filteredCommunities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Users className="w-16 h-16 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">
              {activeTab === 'my' ? "You haven't joined any communities yet" : 'No communities found'}
            </p>
            <Button 
              variant="outline" 
              className="mt-4 rounded-full"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Create First Community
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredCommunities.map((community) => (
              <Card 
                key={community.id} 
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                data-testid={`community-${community.id}`}
              >
                <CardContent className="p-0">
                  {/* Cover Image */}
                  <div 
                    className="h-24 bg-gradient-to-r from-primary/30 to-violet-500/30 relative"
                    style={community.cover_image ? { 
                      backgroundImage: `url(${community.cover_image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    } : {}}
                  >
                    {community.is_private && (
                      <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                        <Lock className="w-3 h-3 text-yellow-400" />
                        <span className="text-xs text-white">Private</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <Link to={`/communities/${community.id}`}>
                      <h3 className="font-bold text-lg hover:text-primary transition-colors">
                        {community.name}
                      </h3>
                    </Link>
                    {community.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {community.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {formatNumber(community.members_count)} members
                        </span>
                        {community.is_admin && (
                          <span className="flex items-center gap-1 text-primary">
                            <Shield className="w-4 h-4" />
                            Admin
                          </span>
                        )}
                      </div>
                      
                      {community.is_member ? (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="rounded-full"
                            onClick={(e) => {
                              e.preventDefault();
                              handleLeave(community.id);
                            }}
                          >
                            <LogOut className="w-4 h-4 mr-1" />
                            Leave
                          </Button>
                          <Button 
                            size="sm" 
                            className="rounded-full"
                            onClick={() => navigate(`/communities/${community.id}`)}
                          >
                            View
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          size="sm" 
                          className="rounded-full"
                          onClick={(e) => {
                            e.preventDefault();
                            handleJoin(community.id);
                          }}
                          disabled={community.is_private}
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          {community.is_private ? 'Request Join' : 'Join'}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Community Modal */}
      <CreateCommunityModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(newCommunity) => {
          setCommunities(prev => [newCommunity, ...prev]);
          setMyCommunities(prev => [newCommunity, ...prev]);
          navigate(`/communities/${newCommunity.id}`);
        }}
      />
    </div>
  );
}
